import * as icons from '../../assets/icons'
import { DefaultManager, ManagerProps } from './components/Manager/Manager'
import { FC, useEffect, useMemo } from 'react'
import { FileUploadInput } from '../util/SettingsUtil'
import { FlowpageLinkCard } from '../flowpage/FlowpageLinkCard'
import { FlowpageProps } from './types/FlowpageProps'
import { FormFields } from './types'
import { FormProps, StyledFormV2 } from './components/Layout'
import { FormTitle } from './components/Title'
import { ImageDisplayToggle } from './Destination/ImageDisplayToggle'
import { ImageEdit } from './components/ImageEdit'
import { LinkProvider } from '@dtx-company/inter-app/src/constants/linkTypes'
import { LinkStyleGated } from './components/LinkStyle/LinkStyleGated'
import { MaskType } from '@dtx-company/shared-components'
import { SettingsAndSaveLink } from './components/Settings'
import { Box, Button, Stack, TextField, Typography } from '@dtx-company/design-system/src'
import { TITLE_MAX, TITLE_MAX_ERROR } from './validation'
import { dataURItoBlob } from '../../utils/imageEditor'
import { fileUpload } from '../../assets/logos'
import { fileUploadColor } from '@dtx-company/app/shared/code/src/assets'
import { getValidatedActionData } from './typeUtils'
import { useForm } from 'react-hook-form'
import { useLinkThemeState } from './components/LinkStyle/hooks/useLinkThemeState'
import { useSetValueWithPreview } from './utils'
import { useSubmitWidget } from './submitUtils'
import { v4 as uuid } from 'uuid'
import Image from 'next/image'

interface PDFFormFields {
  pdf: File | null
  title: string
  image: string | null
  displayType: string
}
const { TITLE, IMAGE, DISPLAY_TYPE } = FormFields
enum PDFField {
  PDF = 'pdf'
}

export const Form: FC<FormProps> = ({ order, curr, handleClose }: FormProps) => {
  const actionData = getValidatedActionData<'pdf'>(curr?.actionData, 'link')

  const { linkTheme, setLinkTheme } = useLinkThemeState({
    defaultLinkTheme: curr?.linkTheme ?? null
  })

  const {
    register,
    setValue: setFormValue,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors
  } = useForm<PDFFormFields>({
    defaultValues: {
      title: curr?.title || '',
      pdf: null,
      image: curr?.thumbNailImgUrl || '',
      displayType: curr?.displayType || 'default'
    },
    mode: 'onBlur'
  })
  const id = curr ? curr.id : uuid()
  const submitWidget = useSubmitWidget()
  const onSubmit = async ({ title, pdf, image, displayType }: PDFFormFields): Promise<void> => {
    const newActionData = pdf ? { link: URL.createObjectURL(pdf) } : curr?.actionData
    await submitWidget({
      image: image || '',
      curr,
      linkTheme,
      actionData: newActionData || {},
      widgetType: 'pdf',
      fields: {
        id,
        description: '',
        title,
        order,
        displayType: displayType || 'default'
      }
    })

    handleClose()
  }

  useEffect(() => {
    register(DISPLAY_TYPE)
    register(TITLE, {
      required: true,
      maxLength: {
        value: TITLE_MAX,
        message: TITLE_MAX_ERROR
      }
    })
    register(IMAGE)
    register(PDFField.PDF, {
      required: actionData?.link ? false : true
    })
  }, [actionData?.link, register])
  const watchAll = watch()

  const disabled = (!curr && !watchAll?.pdf) || watchAll?.title?.length === 0
  const edit = Boolean(curr)
  const previewLink = useMemo(
    () => ({
      provider: LinkProvider.WIDGET,
      type: 'pdf',
      embed: false,
      __typename: 'Link' as const,
      actionData: {},
      title: watchAll.title,
      thumbNailImgUrl: watchAll.image,
      id,
      order,
      linkTheme,
      active: true,
      description: '',
      displayType: watchAll.displayType
    }),
    [id, linkTheme, order, watchAll.image, watchAll.title, watchAll.displayType]
  )

  const { setValue, setLinkThemeValue } = useSetValueWithPreview(
    previewLink,
    setFormValue,
    setLinkTheme
  )

  const fileURL = watchAll.pdf?.name ? URL.createObjectURL(watchAll.pdf) : actionData?.link

  return (
    <StyledFormV2 onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={4}>
        <FormTitle
          icon={<Image width={24} height={24} src={icons.pdfColor} alt="" />}
          title={`${edit ? 'Edit this' : 'Add a'} File`}
        />

        <TextField
          label="Link title"
          value={watchAll.title}
          error={Boolean(errors?.title)}
          helperText={errors?.title?.message}
          onChange={e => setValue(TITLE, e.target.value)}
        />

        <FileUploadInput
          label={fileURL ? 'Replace file' : 'Upload file'}
          accept="application/pdf,video/mp4,audio/*"
          maxSize={20}
          handleUpload={e => {
            setValue(PDFField.PDF, e)
            clearErrors(PDFField.PDF)
          }}
        />

        {fileURL && (
          <Box>
            <Button href={fileURL}>Download File</Button>
          </Box>
        )}

        <ImageDisplayToggle
          displayType={watchAll.displayType}
          onChange={(_, type) => {
            setValue(FormFields.IMAGE, undefined)
            setValue(FormFields.DISPLAY_TYPE, type)
          }}
          fetching={false}
        />

        {watchAll.displayType === 'noImage' ? null : (
          <ImageEdit
            imageUrl={watchAll.image || fileUploadColor}
            setValue={setValue}
            defaultImage={fileUploadColor}
            maskType={watchAll.displayType === 'default' ? MaskType.SQUARE : MaskType.FEATURED}
          />
        )}

        {errors?.pdf && <Typography>{errors?.pdf?.message || 'File Link is Required'}</Typography>}

        <LinkStyleGated linkTheme={linkTheme} setLinkTheme={setLinkThemeValue} />

        <SettingsAndSaveLink disabled={disabled} curr={curr} handleClose={handleClose} />
      </Stack>
    </StyledFormV2>
  )
}

export const Flowpage: FC<FlowpageProps> = ({
  link,
  preview = false,
  editLinkMode = false,
  isLockedTemplateLink
}: FlowpageProps) => {
  const ad = getValidatedActionData<'pdf'>(link.actionData, 'link')

  return (
    <FlowpageLinkCard
      link={{
        ...link,
        actionData: {
          link: ad?.link.startsWith('data:')
            ? URL.createObjectURL(dataURItoBlob(ad.link))
            : ad?.link || ''
        }
      }}
      isLockedTemplateLink={isLockedTemplateLink}
      widgetId="pdf"
      preview={preview}
      editLinkMode={editLinkMode}
      iconSrc={link.thumbNailImgUrl || fileUploadColor}
    />
  )
}

export const Manager: FC<ManagerProps> = ({ link, editWidget, key, handle }: ManagerProps) => {
  return <DefaultManager iconUrl={fileUpload} handle={handle} editWidget={editWidget} link={link} />
}
